import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import SkillFormDialog from 'features/feature_cv/components/dialogs/SkillFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

function SkillsSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_skills', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/userskill/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteUserSkill(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_skills', cvId]);
            }
        }
    );

    const skills = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this skill?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingSkill(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Skills ({skills.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingSkill(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Skill
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {skills.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No skills added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {skills.map((skill) => (
                        <Paper key={skill.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {formatText(skill.title)}
                                        </Typography>
                                        {skill.years_of_experience && (
                                            <Chip label={`${skill.years_of_experience} years`} size="small" variant="outlined" />
                                        )}
                                    </Stack>
                                    {skill.skill?.title && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Category: {formatText(skill.skill.title)}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingSkill(skill);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(skill.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <SkillFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingSkill(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                skill={editingSkill}
            />
        </Paper>
    );
}

export default SkillsSection;

